"use client";

import React, { useMemo, useState } from "react";
import { BookingSession, useBookingStore } from "@/lib/bookingStore";
import { ClassCard } from "./ClassCard";
import { StudioTag } from "./StudioTag";
import { formatDayLabel } from "@/lib/schedule";
import { Filter, Calendar, Sparkles, CalendarSearch } from "lucide-react";

interface WeeklyAgendaProps {
  initialSessions: BookingSession[];
}

export const WeeklyAgenda: React.FC<WeeklyAgendaProps> = ({ initialSessions }) => {
  const { selectedStudio, selectedType, setSelectedStudio, setSelectedType } =
    useBookingStore();

  const studiosList = [
    { label: "All Studios", value: "All" },
    { label: "Legian", value: "legian" },
    { label: "Berawa", value: "berawa" },
    { label: "Sanur", value: "sanur" },
    { label: "Pererenan", value: "pererenan" },
  ];

  const typesList = [
    { label: "All Classes", value: "All" },
    { label: "Group", value: "Group" },
    { label: "Private", value: "Private" },
    { label: "Couples", value: "Couples" },
    { label: "Circuit", value: "Circuit" },
  ];

  // Group unique days, sorted chronologically (date is "YYYY-MM-DD" so string sort works).
  // Labels are derived from the date in WITA (formatDayLabel), never the stored dayLabel,
  // so they stay correct as the schedule rolls forward (revision 1.1.2 item 5).
  const days = useMemo(() => {
    const now = new Date();
    const map = new Map<string, { label: string; date: string }>();
    initialSessions.forEach((s) => {
      if (!map.has(s.date)) {
        map.set(s.date, { label: formatDayLabel(s.date, now), date: s.date });
      }
    });
    return Array.from(map.values()).sort((a, b) => a.date.localeCompare(b.date));
  }, [initialSessions]);

  // Filter sessions
  const filteredSessionsByDay = useMemo(() => {
    const result: Record<string, BookingSession[]> = {};

    days.forEach((day) => {
      result[day.date] = initialSessions.filter((s) => {
        if (s.date !== day.date) return false;
        const studioMatch = selectedStudio === "All" || s.studioSlug === selectedStudio;
        const typeMatch = selectedType === "All" || s.classType === selectedType;
        return studioMatch && typeMatch;
      });
    });

    return result;
  }, [initialSessions, days, selectedStudio, selectedType]);

  // Compute jump-to-day dots
  const dayStatus = useMemo(() => {
    const status: Record<string, "open" | "waitlist" | "empty"> = {};

    days.forEach((day) => {
      const rows = filteredSessionsByDay[day.date] || [];
      if (rows.length === 0) {
        status[day.date] = "empty";
      } else {
        const hasOpenSpot = rows.some((r) => r.capacity - r.spotsBooked > 0);
        status[day.date] = hasOpenSpot ? "open" : "waitlist";
      }
    });

    return status;
  }, [days, filteredSessionsByDay]);

  const scrollToDay = (date: string) => {
    const el = document.getElementById(`day-section-${date}`);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const [dateNotice, setDateNotice] = useState<string | null>(null);

  const handleDatePick = (value: string) => {
    if (!value) return;
    if (days.some((d) => d.date === value)) {
      setDateNotice(null);
      scrollToDay(value);
    } else {
      setDateNotice("No classes scheduled on that date yet — try one of the highlighted days below.");
    }
  };

  return (
    <div className="w-full space-y-8">
      {/* Sticky Filter Header */}
      <div className="sticky top-16 sm:top-20 z-20 py-4 glass-panel rounded-2xl p-4 sm:p-6 border border-[#84979e]/20 shadow-sm">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          {/* Studio Filter */}
          <div className="flex items-center gap-2 overflow-x-auto no-scrollbar pb-1 md:pb-0">
            <span className="text-xs font-bold text-outline uppercase tracking-wider hidden sm:inline-block mr-1">
              Studio:
            </span>
            {studiosList.map((st) => {
              const isSelected = selectedStudio === st.value;
              return (
                <button
                  key={st.value}
                  onClick={() => setSelectedStudio(st.value)}
                  className={`px-3.5 py-1.5 rounded-pill text-xs font-semibold whitespace-nowrap transition-all active:scale-[0.97] ${
                    isSelected
                      ? "bg-primary text-white shadow-sm"
                      : "bg-surface-container text-on-surface-variant hover:bg-surface-container-high"
                  }`}
                >
                  {st.label}
                </button>
              );
            })}
          </div>

          {/* Class Type Filter */}
          <div className="flex items-center gap-1.5 overflow-x-auto no-scrollbar pb-1 md:pb-0">
            <span className="text-xs font-bold text-outline uppercase tracking-wider hidden sm:inline-block mr-1">
              Type:
            </span>
            {typesList.map((tp) => {
              const isSelected = selectedType === tp.value;
              return (
                <button
                  key={tp.value}
                  onClick={() => setSelectedType(tp.value)}
                  className={`px-3 py-1.5 rounded-pill text-xs font-semibold whitespace-nowrap transition-all active:scale-[0.97] ${
                    isSelected
                      ? "bg-secondary text-white shadow-sm"
                      : "bg-white border border-[#84979e]/25 text-on-surface-variant hover:bg-surface-container"
                  }`}
                >
                  {tp.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Jump-To-Day Pills with Real-Time Availability Dots, plus a free-pick date field */}
        <div className="mt-4 pt-3 border-t border-[#84979e]/15 flex flex-wrap items-center gap-2">
          <span className="text-xs text-outline font-medium flex items-center gap-1 shrink-0">
            <Calendar className="w-3.5 h-3.5" /> Jump to:
          </span>
          <div className="flex items-center gap-2 overflow-x-auto no-scrollbar">
          {days.map((day) => {
            const st = dayStatus[day.date];
            return (
              <button
                key={day.date}
                onClick={() => scrollToDay(day.date)}
                className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-medium bg-white/80 border border-[#84979e]/20 text-on-surface hover:border-primary/40 transition-colors shrink-0"
              >
                {st === "open" && (
                  <span className="w-2 h-2 rounded-full bg-[#506531]" title="Spots Available" />
                )}
                {st === "waitlist" && (
                  <span className="w-2 h-2 rounded-full bg-[#715c09]" title="Waitlist Only" />
                )}
                {st === "empty" && (
                  <span className="w-2 h-2 rounded-full bg-outline/40" title="No classes matching filters" />
                )}
                <span>{day.label}</span>
              </button>
            );
          })}
          </div>

          {/* Free date pick — jumps to any seeded day, not just the pills above */}
          <label className="flex items-center gap-1.5 ml-auto shrink-0 text-xs font-medium text-outline">
            <CalendarSearch className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">Pick a date:</span>
            <input
              type="date"
              min={days[0]?.date}
              max={days[days.length - 1]?.date}
              onChange={(e) => handleDatePick(e.target.value)}
              className="px-2 py-1 rounded-lg border border-[#84979e]/25 bg-white/80 text-xs text-on-surface"
              aria-label="Jump to a specific date"
            />
          </label>
        </div>

        {dateNotice && (
          <p className="mt-2 text-xs text-tertiary bg-tertiary-fixed/40 rounded-lg px-3 py-1.5 inline-block">
            {dateNotice}
          </p>
        )}
      </div>

      {/* Desktop 3-Column Board & Mobile Continuous List */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {days.map((day) => {
          const sessions = filteredSessionsByDay[day.date] || [];
          return (
            <section
              key={day.date}
              id={`day-section-${day.date}`}
              className="scroll-mt-48 space-y-4"
            >
              {/* Day Header */}
              <div className="flex items-center justify-between pb-2 border-b border-[#84979e]/20">
                <div>
                  <h2 className="font-headline font-semibold text-xl text-on-surface">
                    {day.label}
                  </h2>
                  <span className="text-xs text-outline font-medium">{day.date}</span>
                </div>
                <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-surface-container text-on-surface-variant">
                  {sessions.length} {sessions.length === 1 ? "Session" : "Sessions"}
                </span>
              </div>

              {/* Sessions List or Empty State */}
              {sessions.length > 0 ? (
                <div className="space-y-4">
                  {sessions.map((sess, idx) => (
                    <ClassCard key={`${sess.date}-${sess.time}-${idx}`} session={sess} />
                  ))}
                </div>
              ) : (
                <div className="rounded-2xl border border-dashed border-[#84979e]/30 p-8 text-center bg-white/50 space-y-2">
                  <p className="text-sm font-medium text-on-surface-variant">
                    No classes match your active filters on {day.label}.
                  </p>
                  <button
                    onClick={() => {
                      setSelectedStudio("All");
                      setSelectedType("All");
                    }}
                    className="text-xs font-semibold text-primary underline hover:text-[#2c4e6e]"
                  >
                    Reset all filters
                  </button>
                </div>
              )}
            </section>
          );
        })}
      </div>
    </div>
  );
};
